import React, { useState, useEffect } from 'react';
import { ref, onValue, push, remove } from 'firebase/database';
import { db } from '../../firebase';
import { Course, Exam } from '../../types';

const CURRENT_SEMESTER = '2025-2026';

const typeLabels: Record<string, string> = {
  midterm: 'Vize', final: 'Final', quiz: 'Quiz', makeup: 'Bütünleme',
};

interface Props { courses: Course[]; }

const ExamsTab: React.FC<Props> = ({ courses }) => {
  const [exams, setExams] = useState<Exam[]>([]);
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState({
    courseId: '', type: 'midterm', date: '', time: '', location: '', semester: CURRENT_SEMESTER,
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    return onValue(ref(db, 'exams'), snap => {
      const d = snap.val();
      setExams(d
        ? Object.entries(d).map(([id, v]) => ({ id, ...(v as Exam) })).sort((a, b) => a.date.localeCompare(b.date))
        : []);
    });
  }, []);

  const selectedCourse = courses.find(c => c.id === form.courseId);

  const handleAdd = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedCourse) return;
    setSaving(true);
    await push(ref(db, 'exams'), {
      courseId: form.courseId,
      courseCode: selectedCourse.code,
      courseName: selectedCourse.name,
      type: form.type,
      date: form.date,
      time: form.time,
      location: form.location,
      semester: form.semester,
    });
    setForm({ courseId: '', type: 'midterm', date: '', time: '', location: '', semester: CURRENT_SEMESTER });
    setAdding(false);
    setSaving(false);
  };

  const handleDelete = async (id: string) => {
    if (window.confirm('Bu sınavı silmek istediğinizden emin misiniz?')) {
      await remove(ref(db, `exams/${id}`));
    }
  };

  return (
    <>
      <h2 className="admin-page-title">Sınav Takvimi</h2>
      <p className="admin-page-sub">{exams.length} sınav kayıtlı</p>

      {!adding ? (
        <button className="a-btn a-btn-add" onClick={() => setAdding(true)}>+ Yeni Sınav Ekle</button>
      ) : (
        <div className="admin-card a-form-card">
          <div className="admin-card-header"><h3>Yeni Sınav</h3></div>
          <form onSubmit={handleAdd} className="a-form">
            <div className="a-form-row">
              <div className="a-form-group a-form-group-wide">
                <label>Ders</label>
                <select
                  value={form.courseId}
                  onChange={e => setForm(f => ({ ...f, courseId: e.target.value }))}
                  required
                  className="a-select"
                >
                  <option value="">Ders Seçin</option>
                  {courses.map(c => (
                    <option key={c.id} value={c.id}>{c.code} — {c.name}</option>
                  ))}
                </select>
              </div>
              <div className="a-form-group">
                <label>Sınav Türü</label>
                <select
                  value={form.type}
                  onChange={e => setForm(f => ({ ...f, type: e.target.value }))}
                  className="a-select"
                >
                  <option value="midterm">Vize</option>
                  <option value="final">Final</option>
                  <option value="quiz">Quiz</option>
                  <option value="makeup">Bütünleme</option>
                </select>
              </div>
            </div>
            <div className="a-form-row">
              <div className="a-form-group">
                <label>Tarih</label>
                <input type="date" value={form.date} onChange={e => setForm(f => ({ ...f, date: e.target.value }))} required />
              </div>
              <div className="a-form-group">
                <label>Saat</label>
                <input type="time" value={form.time} onChange={e => setForm(f => ({ ...f, time: e.target.value }))} required />
              </div>
              <div className="a-form-group">
                <label>Yer / Salon</label>
                <input
                  value={form.location}
                  onChange={e => setForm(f => ({ ...f, location: e.target.value }))}
                  placeholder="Salon A-101"
                  required
                />
              </div>
              <div className="a-form-group">
                <label>Dönem</label>
                <input value={form.semester} onChange={e => setForm(f => ({ ...f, semester: e.target.value }))} required />
              </div>
            </div>
            <div className="a-form-actions">
              <button type="submit" className="a-btn a-btn-save" disabled={saving}>
                {saving ? 'Kaydediliyor...' : 'Kaydet'}
              </button>
              <button type="button" className="a-btn a-btn-cancel" onClick={() => setAdding(false)}>İptal</button>
            </div>
          </form>
        </div>
      )}

      <div className="admin-card">
        <div className="a-table-wrap">
          <table>
            <thead>
              <tr>
                <th>Ders Kodu</th><th>Ders Adı</th><th>Tür</th>
                <th>Tarih</th><th>Saat</th><th>Yer</th><th>Dönem</th><th></th>
              </tr>
            </thead>
            <tbody>
              {exams.length === 0 ? (
                <tr><td colSpan={8} className="a-empty">Kayıtlı sınav bulunmamaktadır.</td></tr>
              ) : exams.map(ex => (
                <tr key={ex.id}>
                  <td className="a-code-cell">{ex.courseCode}</td>
                  <td className="a-name-cell">{ex.courseName}</td>
                  <td><span className={`badge b-exam-${ex.type}`}>{typeLabels[ex.type]}</span></td>
                  <td style={{ whiteSpace: 'nowrap' }}>
                    {new Date(ex.date + 'T00:00:00').toLocaleDateString('tr-TR')}
                  </td>
                  <td>{ex.time}</td>
                  <td>{ex.location}</td>
                  <td>{ex.semester}</td>
                  <td>
                    <button className="a-btn a-btn-delete" onClick={() => handleDelete(ex.id!)}>Sil</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
};

export default ExamsTab;
